/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetRiskSignalsRequest,
  GetRiskSignalsSecurity,
} from "../models/operations/getrisksignals.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRiskSignalsQuery,
  prefetchRiskSignals,
  queryKeyRiskSignals,
  RiskSignalsQueryData,
} from "./riskSignals.core.js";
export {
  buildRiskSignalsQuery,
  prefetchRiskSignals,
  queryKeyRiskSignals,
  type RiskSignalsQueryData,
};

export type RiskSignalsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getRiskSignals risk
 *
 * @remarks
 * Get clustered risk signals — findings grouped by rule and ranked by severity score — plus window-level KPI stats and the exposure breakdown by category. Powers the Watchdog page. Served from the ClickHouse findings store.
 */
export function useRiskSignals(
  request?: GetRiskSignalsRequest | undefined,
  security?: GetRiskSignalsSecurity | undefined,
  options?: QueryHookOptions<RiskSignalsQueryData, RiskSignalsQueryError>,
): UseQueryResult<RiskSignalsQueryData, RiskSignalsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRiskSignalsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getRiskSignals risk
 *
 * @remarks
 * Get clustered risk signals — findings grouped by rule and ranked by severity score — plus window-level KPI stats and the exposure breakdown by category. Powers the Watchdog page. Served from the ClickHouse findings store.
 */
export function useRiskSignalsSuspense(
  request?: GetRiskSignalsRequest | undefined,
  security?: GetRiskSignalsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RiskSignalsQueryData,
    RiskSignalsQueryError
  >,
): UseSuspenseQueryResult<RiskSignalsQueryData, RiskSignalsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRiskSignalsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRiskSignalsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      from?: Date | undefined;
      to?: Date | undefined;
      mcpServerId?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: RiskSignalsQueryData,
): RiskSignalsQueryData | undefined {
  const key = queryKeyRiskSignals(...queryKeyBase);

  return client.setQueryData<RiskSignalsQueryData>(key, data);
}

export function invalidateRiskSignals(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      from?: Date | undefined;
      to?: Date | undefined;
      mcpServerId?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "signals", "get", ...queryKeyBase],
  });
}

export function invalidateAllRiskSignals(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "signals", "get"],
  });
}
